import React, { useEffect, useState } from 'react';
import {
  Form,
  InputNumber,
  Input,
  Layout,
  message,
  Typography,
  Row,
  Col,
  Modal,
  Skeleton,
  Checkbox,
  DatePicker,
} from 'antd';
import 'react-quill-new/dist/quill.snow.css';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

import DiscountApi from 'services/discount';

import PdButton from 'components/pd-button';
import { PageSpinner } from 'components/page-spinner';

import './style.scss';
import { get } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';

const { Content } = Layout;
const { Title } = Typography;
const { confirm } = Modal;
const { Search } = Input;

const FIELDS = {
  TITLE: 'title',
  PERCENTAGE: 'deduction_percentage',
  DISCOUNT_GROUP: 'discount_groups_attributes',
  START_DATE: 'start_date',
  END_DATE: 'end_date',
};

export function DiscountCreateEdit() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [value, setValue] = useState({});
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [productList, setProductList] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [loadModal, setLoadModal] = useState(false);
  const [productNextPage, setProductNextPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(1);

  const [formLayout] = useState('vertical');
  const location = useLocation();

  const urlParams = new URLSearchParams(location.search);
  const id = urlParams.get('id');

  const formItemLayout =
    formLayout === 'horizontal'
      ? {
          labelCol: { span: 4 },
          wrapperCol: { span: 14 },
        }
      : null;

  const buttonItemLayout =
    formLayout === 'horizontal'
      ? {
          wrapperCol: { span: 14, offset: 4 },
        }
      : null;

  async function getDiscount() {
    try {
      setLoadingPage(true);
      if (id) {
        const { data } = await DiscountApi.getDiscount(id);
        const DiscountDetail = data.data;
        setProductList(get(DiscountDetail, 'discount_groups.products', []));
        setValue(DiscountDetail);
        form.setFieldsValue({
          [FIELDS.TITLE]: DiscountDetail.title,
          [FIELDS.PERCENTAGE]: DiscountDetail.deduction_percentage,
          [FIELDS.START_DATE]: dayjs(DiscountDetail.start_date),
          [FIELDS.END_DATE]: dayjs(DiscountDetail.end_date),
        });
      }
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      setLoadingPage(false);
    }
  }
  useEffect(() => {
    if (id) {
      getDiscount();
    } else {
      setValue([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, navigate, id]);

  async function onFinish(values) {
    try {
      values[FIELDS.DISCOUNT_GROUP] = productList.map((product) => {
        product.object_id = product.id;
        product.object_type = 'Product';
        return product;
      });
      setLoadingButton(true);
      if (id) {
        values.weight = Number(values.weight) * 1000;
        await DiscountApi.updateDiscount(id, { ...value, ...values });
        message.success('Successfully updated discount');
      } else {
        await DiscountApi.createDiscount({ ...value, ...values });
        message.success('Successfully created discount');
      }
      navigate({
        pathname: '/discounts',
      });
    } catch (err) {
      const errMessage = get(
        get(get(err, 'response', {}), 'data', {}),
        'message',
        ''
      );
      if (errMessage) {
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    } finally {
      setLoadingButton(false);
    }
  }

  function changeValue(e, key) {
    if (e) {
      setValue((prevState) => ({
        ...prevState,
        [key]: e,
      }));
    }
  }

  function deleteProduct(id) {
    confirm({
      title: 'Are you sure delete this product?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        const newProductList = productList.filter(
          (product) => Number(get(product, 'id', '')) !== Number(id)
        );
        setProductList(newProductList);
      },
      onCancel() {},
    });
  }

  async function fetchAllProduct(
    page = 1,
    q = '',
    loadmore,
    total = 4,
    sort_by = 'created_at desc'
  ) {
    try {
      const { data } = await DiscountApi.getProductList(
        page,
        q,
        sort_by,
        total
      );
      const productData = data.data;
      const metadata = data.metadata;

      const newProduct = productData.map((product) => {
        if (productList.some((e) => e.id === product.id)) {
          product.checked = true;
        } else {
          product.checked = false;
        }
        return product;
      });

      setProductNextPage(metadata.next_cursor);
      if (loadmore) {
        setAllProducts((prevstate) => [...prevstate, ...newProduct]);
      } else {
        setAllProducts(newProduct);
      }
    } catch (err) {
      if (err.response.data.message) {
        const errMessage = err.response.data.message;
        message.error(errMessage);
      } else {
        message.error('Cannot connect to server, please check connection');
      }
    }
  }

  function changePage(page) {
    setPage(page);
    fetchAllProduct(page, searchValue, true);
  }

  async function showModal() {
    setLoadModal(true);
    setModalVisible(true);

    await fetchAllProduct(productNextPage);
    setLoadModal(false);
  }

  function onChecked(e, i) {
    const newProductList = allProducts.slice(0);
    newProductList[i].checked = e.target.checked;
    setAllProducts(newProductList);
  }

  function submitModal() {
    const newProduct = allProducts
      .slice(0)
      .filter(
        (product) =>
          product.checked &&
          productList.filter((e) => e.id === product.id).length === 0
      );
    setProductList((prevState) => [...newProduct, ...prevState]);
    exitModal();
  }

  function searchOnClick() {
    fetchAllProduct(1, searchValue);
  }

  function exitModal() {
    setProductNextPage(1);
    setSearchValue('');
    setPage(1);
    setAllProducts([]);
    setModalVisible(false);
  }

  if (loadingPage) {
    return <PageSpinner />;
  }

  return (
    <Layout className="pd-cms-discount main-container">
      <div className="container">
        <div className="content">
          <div className="pageTitle">{id ? 'Edit' : 'Create New'} Discount</div>
          <Content className="pageContent">
            <Form form={form} layout={formLayout} onFinish={onFinish}>
              <Title className="pageSubtitle" level={3}>
                Information Discount
              </Title>
              <div className="section-container">
                <div className="inner-section">
                  <Form.Item
                    label="Title"
                    name={FIELDS.TITLE}
                    {...formItemLayout}
                    rules={[
                      { required: true, message: 'Please input Title!' },
                    ]}>
                    <Input
                      placeholder="Write discount title here"
                      onChange={(e) => changeValue(e, FIELDS.TITLE)}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Percentage"
                    name={FIELDS.PERCENTAGE}
                    {...formItemLayout}
                    rules={[
                      { required: true, message: 'Please input Percentage!' },
                    ]}>
                    <InputNumber
                      className="inputForm"
                      placeholder="Write discount percentage here"
                      onChange={(e) => changeValue(e, FIELDS.PERCENTAGE)}
                    />
                  </Form.Item>
                  <Row gutter={20}>
                    <Col span={12}>
                      <Form.Item
                        label="Start Date"
                        name={FIELDS.START_DATE}
                        {...formItemLayout}
                        rules={[
                          {
                            required: true,
                            message: 'Please input Start Date!',
                          },
                        ]}>
                        <DatePicker format="DD/MM/YYYY" className="inputForm" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="End Date"
                        name={FIELDS.END_DATE}
                        {...formItemLayout}
                        rules={[
                          {
                            required: true,
                            message: 'Please input End Date!',
                          },
                        ]}>
                        <DatePicker format="DD/MM/YYYY" className="inputForm" />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </div>
              <Title className="pageSubtitle" level={3}>
                Discount Group
              </Title>
              <div className="section-container">
                <div className="inner-section">
                  <div>
                    <Row gutter={20}>
                      <Col span={20}>
                        <label title="Products">Products</label>
                      </Col>
                      <Col span={4}>
                        <PdButton
                          type="primary"
                          full
                          onClick={() => showModal()}>
                          Add Product
                        </PdButton>
                      </Col>
                      <Col span={24} className="pd-cms-discount--product-list">
                        {productList.map((product, i) => (
                          <Row
                            key={i}
                            className="product-row"
                            gutter={20}
                            type="flex"
                            align="middle">
                            <Col span={3}>
                              <div
                                className="product--picture"
                                style={{
                                  backgroundImage: `url(${get(
                                    product,
                                    'preview.secure_url',
                                    ''
                                  )}), url(/img/logo/no-image.png)`,
                                }}></div>
                            </Col>
                            <Col span={17}>
                              <span className="bold">
                                {get(product, 'title', '')}
                              </span>
                            </Col>
                            <Col span={4}>
                              <PdButton
                                type="danger"
                                outline
                                full
                                onClick={() =>
                                  deleteProduct(get(product, 'id', ''))
                                }>
                                Delete
                              </PdButton>
                            </Col>
                          </Row>
                        ))}
                      </Col>
                    </Row>
                  </div>
                </div>
                <Form.Item {...buttonItemLayout}>
                  <div className="buttonSection mt-40">
                    <PdButton
                      htmlType="submit"
                      loading={loadingButton}
                      type="primary"
                      full>
                      Publish
                    </PdButton>
                  </div>
                </Form.Item>
              </div>
            </Form>
          </Content>
        </div>
      </div>
      <Modal
        className="modal--productList"
        open={modalVisible}
        onOk={exitModal}
        wrapClassName="hidden-wrap-modal"
        centered
        onCancel={exitModal}>
        {loadModal ? (
          <div className="modal-container">
            <Skeleton active paragraph={{ rows: 8 }} />
          </div>
        ) : (
          <div className="modal-container">
            <Row>
              <Col span={24} className="header">
                <h1 className="bold">Add Product</h1>
              </Col>
              <Col span={24}>
                <Search
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  enterButton
                  className="search-product"
                  placeholder="Search Product List"
                  onSearch={searchOnClick}
                />
              </Col>
              <Col span={24} className="product-catalog">
                {allProducts.map((product, i) => (
                  <Row key={i} className="product-item">
                    <Col span={1}>
                      <Checkbox
                        checked={product.checked}
                        onChange={(e) => onChecked(e, i)}
                      />
                    </Col>
                    <Col span={23} className="product-title">
                      <span className=" bold">{product.title}</span>
                    </Col>
                  </Row>
                ))}
                <Row>
                  <Col span={8} offset={8}>
                    <div className="center-button">
                      {productNextPage !== -1 && (
                        <PdButton
                          type="primary"
                          outline
                          style={{ height: 32 }}
                          onClick={() => changePage(Number(page) + 1)}>
                          Load More
                        </PdButton>
                      )}
                    </div>
                  </Col>
                </Row>
              </Col>
              <Col span={10} offset={7} className="button-container">
                <PdButton type="primary" full onClick={submitModal}>
                  Add Product
                </PdButton>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </Layout>
  );
}

export default DiscountCreateEdit;
